import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useCompany } from '../../contexts/CompanyContext';
import { useMobile } from '../../contexts/MobileContext';
import { useAccountant } from '../../contexts/AccountantContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Avatar, AvatarFallback } from '../ui/avatar';
import { Sun, Moon, SignOut, User, List, Crown, Star, Briefcase, Buildings, Calculator, Sparkle, NotePencil, Question } from '@phosphor-icons/react';
import { Link } from 'react-router-dom';
import NotificationBell from './NotificationBell';
import UniversalSearch from './UniversalSearch';
import CalculatorModal from '../common/CalculatorModal';
import QuickNotesModal from '../common/QuickNotesModal';
import { reopenWelcomeWizard } from '../onboarding/WelcomeWizard';
import { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export default function Topbar() {
  const { user, logout, token } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  const { company, logoUrl } = useCompany();
  const { isMobile, toggleSidebar } = useMobile();
  const { accountantMode, accountantSettings, isInClientContext, activeClient } = useAccountant();
  const [subscription, setSubscription] = useState(null);
  const [calcOpen, setCalcOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      if (!token) return;
      try {
        const res = await axios.get(`${API}/api/platform/subscription/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSubscription(res.data);
      } catch (err) {
        // Subscription status not available
      }
    };
    fetchSubscription();
  }, [token]);

  const getInitials = () => {
    if (!user) return 'U';
    return `${user.first_name?.[0] || ''}${user.last_name?.[0] || ''}`.toUpperCase();
  };

  // Get subscription badge config
  const getSubscriptionBadge = () => {
    if (accountantMode && accountantSettings) {
      const plan = accountantSettings.accountant_plan?.replace('accountant_', '').toUpperCase();
      const planConfig = {
        BASIC: { 
          label: 'Basic', 
          icon: Briefcase, 
          className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
        },
        PRO: { 
          label: 'Pro', 
          icon: Star, 
          className: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0'
        },
        ENTERPRISE: { 
          label: 'Enterprise', 
          icon: Crown, 
          className: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0'
        }
      };
      return planConfig[plan] || planConfig.BASIC;
    }

    if (subscription) {
      const plan = subscription.plan?.toUpperCase();
      const planConfig = {
        TRIAL: { 
          label: 'Trial', 
          icon: null, 
          className: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
        },
        BASIC: { 
          label: 'Basic', 
          icon: null, 
          className: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300 border border-slate-200 dark:border-slate-700'
        },
        PRO: { 
          label: 'Pro', 
          icon: Star, 
          className: 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white border-0'
        },
        ENTERPRISE: { 
          label: 'Enterprise', 
          icon: Crown, 
          className: 'bg-gradient-to-r from-amber-500 to-orange-600 text-white border-0'
        }
      };
      return planConfig[plan] || null;
    }

    return null;
  };

  const subscriptionBadge = getSubscriptionBadge();

  return (
    <header 
      className="h-16 glass-topbar flex items-center justify-between px-4 lg:px-6 sticky top-0 z-30"
      data-testid="topbar"
    >
      {/* Left Section - Hamburger + Company */}
      <div className="flex items-center gap-3">
        {/* Hamburger Menu for Mobile */}
        {isMobile && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="h-9 w-9 lg:hidden"
            data-testid="mobile-menu-toggle"
          >
            <List size={22} weight="bold" />
          </Button>
        )}
        
        {/* Company Name - Mobile shows logo, Desktop shows name */}
        {isMobile ? (
          <div className="flex items-center gap-2">
            {logoUrl ? (
              <div className="w-8 h-8 rounded-lg overflow-hidden bg-muted">
                <img 
                  src={logoUrl} 
                  alt={company?.name || 'Company'} 
                  className="w-full h-full object-contain"
                />
              </div>
            ) : (
              <div className="w-8 h-8 rounded-lg bg-primary flex items-center justify-center">
                <span className="text-primary-foreground font-heading font-bold text-sm">
                  {company?.name?.[0] || 'E'}
                </span>
              </div>
            )}
            <span className="font-heading font-semibold text-sm truncate max-w-[120px]">
              {isInClientContext ? activeClient?.company_name : (company?.name || 'ERP Hub')}
            </span>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {isInClientContext && (
              <Badge variant="outline" className="text-xs bg-[#0f172a] text-white border-0">
                <Buildings size={12} className="mr-1" />
                Managing Client
              </Badge>
            )}
            <span className="text-sm font-medium text-muted-foreground">
              {isInClientContext ? activeClient?.company_name : (company?.name || user?.company_name)}
            </span>
          </div>
        )}
      </div>

      {/* Right Section - Actions */}
      <div className="flex items-center gap-2 lg:gap-3">
        {/* Universal Search */}
        <UniversalSearch />
        
        {/* Theme Toggle */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleTheme}
          className="h-9 w-9"
          data-testid="theme-toggle"
        >
          {isDark ? (
            <Sun size={18} weight="fill" className="text-amber-400" />
          ) : (
            <Moon size={18} weight="fill" />
          )}
        </Button>

        {/* Calculator */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setCalcOpen(true)}
          className="h-9 w-9"
          title="Calculator"
          data-testid="topbar-calculator-btn"
        >
          <Calculator size={18} weight="fill" className="text-[#d4a84b]" />
        </Button>

        {/* Quick Notes (personal scratchpad — sits next to Calculator for
            instant access from anywhere in the app). */}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => setNotesOpen(true)}
          className="h-9 w-9"
          title="Quick Notes"
          data-testid="topbar-notes-btn"
        >
          <NotePencil size={18} weight="fill" className="text-amber-500" />
        </Button>

        {/* Help — opens the User Guide. Available on every screen so users can
            look up any feature or workflow instantly. */}
        <Button
          asChild
          variant="ghost"
          size="icon"
          className="h-9 w-9"
          title="Help &amp; User Guide"
          data-testid="topbar-help-btn"
        >
          <Link to="/dashboard/guide#end-to-end-workflow">
            <Question size={18} weight="fill" className="text-sky-500" />
          </Link>
        </Button>

        {/* Notifications */}
        <NotificationBell />

        {/* Calculator modal */}
        <CalculatorModal open={calcOpen} onClose={() => setCalcOpen(false)} />

        {/* Quick Notes modal */}
        <QuickNotesModal open={notesOpen} onClose={() => setNotesOpen(false)} />

        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button 
              variant="ghost" 
              className="h-9 gap-2 px-2"
              data-testid="user-menu-trigger"
            >
              <Avatar className="h-7 w-7">
                <AvatarFallback className="bg-primary/10 text-primary text-xs font-medium">
                  {getInitials()}
                </AvatarFallback>
              </Avatar>
              <div className="hidden sm:flex flex-col items-start">
                <span className="text-sm font-medium leading-tight">
                  {user?.first_name}
                </span>
                {/* Subscription Badge */}
                {subscriptionBadge && (
                  <Badge 
                    className={`text-[10px] px-1.5 py-0 h-4 font-medium ${subscriptionBadge.className}`}
                    data-testid="subscription-badge"
                  >
                    {subscriptionBadge.icon && <subscriptionBadge.icon size={10} className="mr-0.5" weight="fill" />}
                    {subscriptionBadge.label}
                  </Badge>
                )}
              </div>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-64">
            <div className="px-3 py-2">
              <p className="text-sm font-medium">
                {user?.first_name} {user?.last_name}
              </p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
              
              {/* Subscription Info in Dropdown */}
              {subscriptionBadge && (
                <div className="mt-2 flex items-center gap-2">
                  <Badge 
                    className={`text-xs ${subscriptionBadge.className}`}
                    data-testid="subscription-badge-dropdown"
                  >
                    {subscriptionBadge.icon && <subscriptionBadge.icon size={12} className="mr-1" weight="fill" />}
                    {accountantMode ? 'Accountant ' : ''}{subscriptionBadge.label}
                  </Badge>
                  {subscription?.days_remaining !== undefined && subscription.days_remaining <= 14 && (
                    <span className="text-xs text-amber-600">
                      {subscription.days_remaining} days left
                    </span>
                  )}
                </div>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem data-testid="profile-menu-item">
              <User size={16} className="mr-2" />
              Profile
            </DropdownMenuItem>
            {!user?.is_platform_admin && (
              <DropdownMenuItem
                data-testid="reopen-welcome-tour"
                onClick={() => reopenWelcomeWizard(user?.id)}
              >
                <Sparkle size={16} className="mr-2" weight="fill" />
                Re-open Welcome Tour
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={logout}
              className="text-destructive focus:text-destructive"
              data-testid="logout-menu-item"
            >
              <SignOut size={16} className="mr-2" />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
